import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import HeaderInner from "../../Components/HeaderInner";
import CarRentalPost from "../../Components/CarRentalPost";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CarRentalLoader from "../../Components/Loaders/CarRentalLoader";
import { useTranslation } from "react-i18next";
import Search from "../../Components/Search";
import { useInfiniteQuery, useQuery } from "react-query";
import {
  CarsAPI,
  getCarCompaniesAPI,
  getCarModelsAPI,
  getCarTypesAPI,
  getCarYearsAPI,
} from "../../API/CarsAPI";
import ReactGA from "react-ga4";
import Carosle from "../../Components/Carosle";
import { useBusinessNameContext } from "../../context/BusinessName";
import CustomBusinessServiceLoader from "../../Components/Loaders/CustomBusinessServiceLoader";
import { isClosedNow } from "../../utils/availability";
import { normalizePaymentMethods } from "../../utils/paymentMethods";
import ClosedNotice from "../../new-components/closed-badge/ClosedNotice";

function CarRental() {
  const { businessName } = useBusinessNameContext();

  const lang = sessionStorage.getItem("lang");
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const rawPath = location.pathname;
  const pathParts = rawPath.split("/").filter(Boolean);

  const room_uuid = params.room_uuid || pathParts[0];

  const car_company_uuid =
    params.car_company_uuid || pathParts[pathParts.indexOf("company") + 1];

  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [price, setPrice] = useState(null);

  const { data: companiesData } = useQuery(
    ["car-rental-companies", room_uuid, lang],
    () =>
      getCarCompaniesAPI({
        room_uuid,
        lang,
      }),
    {
      enabled: !!room_uuid,
    }
  );
  const { data, error, isLoading } = useQuery(
    [
      "company-cars",
      lang,
      room_uuid,
      car_company_uuid,
      make?.id || "",
      model?.id || "",
      year?.id || "",
      price || "",
    ],
    () =>
      CarsAPI({
        room_uuid,
        car_company_uuid,
        car_type_id: make?.id || "",
        car_model_id: model?.id || "",
        car_year_id: year?.id || "",
        sort_direction: price || "",
        lang,
      }),
    {
      enabled: !!room_uuid && !!car_company_uuid,
    }
  );

  const { data: typesData } = useQuery(
    ["car-types", lang, room_uuid, car_company_uuid],
    () =>
      getCarTypesAPI({
        room_uuid,
        car_company_uuid,
        lang,
      }),
    {
      enabled: !!room_uuid && !!car_company_uuid,
    }
  );

  const { data: modelsData } = useQuery(
    ["car-models", lang, room_uuid, car_company_uuid, make?.id || ""],
    () =>
      getCarModelsAPI({
        room_uuid,
        car_company_uuid,
        car_rental_type_id: make?.id,
        lang,
      }),
    {
      enabled: !!room_uuid && !!car_company_uuid,
    }
  );

  const { data: yearsData } = useQuery(
    ["car-years", lang, room_uuid, car_company_uuid],
    () =>
      getCarYearsAPI({
        room_uuid,
        car_company_uuid,
        lang,
      }),
    {
      enabled: !!room_uuid && !!car_company_uuid,
    }
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, []);

  if (error && error?.response?.status !== 404) {
    console.error("Error fetching data:", error);
  }

  const response = data?.data;
  const responseData = response?.data;

  const companies = companiesData?.data?.data?.companies || [];

  const currentCompany = companies.find(
    (company) => company.uuid === car_company_uuid
  );
  const companyClosed =
    isClosedNow(currentCompany) || isClosedNow(responseData);

  const paymentMethods = normalizePaymentMethods(
    currentCompany?.payment_methods
  );

  const noCarsMessage =
    response?.message || t("No cars found matching criteria");

  const cars = [...(responseData?.cars || [])].sort((a, b) => {
    if (price === "asc") {
      return Number(a?.price || 0) - Number(b?.price || 0);
    }

    if (price === "desc") {
      return Number(b?.price || 0) - Number(a?.price || 0);
    }

    return 0;
  });
  const carTypes = typesData?.data?.data || [];
  const carModels = modelsData?.data?.data || [];
  const yearsList = yearsData?.data?.data || [];

  const banners = responseData?.banners || [];
  const header_title =
    responseData?.center_header || responseData?.title || t("Car Rental");

  return (
    <>
      <HeaderInner headerName={header_title} />

      <main className="home-main bg-white back-fe pad-top-0 car_rental">
        {isLoading ? (
          <CustomBusinessServiceLoader
            businessName={businessName}
            loader={"carRental"}
          />
        ) : (
          <>
            <Carosle
              className="dot-bar less-margin small-dots"
              headerImages={banners}
            />
            {companyClosed && <ClosedNotice />}

            <Search
              placeholder="Search for A Car"
              room_uuid={room_uuid}
              category="Car Rental"
            />

            <div className="padd-div car-drop-flex flex-div">
              <Dropdown className="car-drop">
                <Dropdown.Toggle
                  variant="success"
                  id="dropdown-make"
                  disabled={carTypes.length === 0}
                >
                  {make?.name ? make.name : t("Make")}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setMake("");
                      setModel("");
                    }}
                  >
                    {t("Make")}
                  </Dropdown.Item>

                  {carTypes.map((type) => (
                    <Dropdown.Item
                      key={type.id}
                      onClick={() => {
                        ReactGA.event({
                          category: "Select Make in car page",
                          action: "Click",
                          value: `make: ${type.name}`,
                        });

                        setMake(type);
                        setModel("");
                      }}
                    >
                      {type.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="car-drop">
                <Dropdown.Toggle
                  variant="success"
                  id="dropdown-model"
                  disabled={carModels.length === 0}
                >
                  {model?.name ? model.name : t("Model")}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setModel("");
                    }}
                  >
                    {t("Model")}
                  </Dropdown.Item>

                  {carModels.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      onClick={() => {
                        setModel(item);

                        ReactGA.event({
                          category: "Select Model in car page",
                          action: "Click",
                          value: `model: ${item.name}`,
                        });
                      }}
                    >
                      {item.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="car-drop">
                <Dropdown.Toggle
                  variant="success"
                  id="dropdown-year"
                  disabled={yearsList.length === 0}
                >
                  {year?.year ? year.year : t("Year")}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setYear("");
                    }}
                  >
                    {t("Year")}
                  </Dropdown.Item>

                  {yearsList.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      onClick={() => {
                        setYear(item);
                      }}
                    >
                      {item.year}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="car-drop">
                <Dropdown.Toggle variant="success" id="dropdown-price">
                  {price
                    ? price === "asc"
                      ? t("Low to high")
                      : t("High to low")
                    : t("Price")}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setPrice(null)}>
                    {t("Price")}
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => setPrice("asc")}>
                    {t("Low to high")}
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => setPrice("desc")}>
                    {t("High to low")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className="spacer-40"></div>

            {cars.length > 0 ? (
              <ul className="display-list car-list two-items v2">
                {cars.map((item) => (
                  <CarRentalPost
                    key={item.uuid}
                    car={item}
                    room_uuid={room_uuid}
                    company_uuid={car_company_uuid}
                    paymentMethods={paymentMethods}
                    closed={companyClosed}
                  />
                ))}
              </ul>
            ) : (
              <div className="text-center py-5">
                <p>{noCarsMessage}</p>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

export default CarRental;
